import type { AdminTarget } from './adminNav';

/**
 * Every admin group's sub-tabs, for the command palette's "jump to a page"
 * results (owner request 2026-10-05). The panels keep their own TABS lists;
 * this is the static copy the palette can search without mounting them.
 *
 * `tab` is the deep-link value (/admin?group=<g>&tab=<tab>) the panel reads.
 * It is left out where the panel does not read a tab from the URL: the entry
 * then just opens the group, and the palette says which tab to pick there.
 * Email, User 360, Users, IP Intel, Leads & Marketing, Content & TRL and
 * Homepage read the lowercase snake_case of the tab label (Content also takes
 * 'sectors' for "Sectors & keywords"); Horizon, Tech URL pages, User tags,
 * Billing, Onboarding and Recrawl have their own names. Ocean reads the
 * snake_case of its tab label too; only its COSMOS matching and Feature
 * access rows list one.
 *
 * The palette shows only what this admin may open (admin/adminAccess.tsx):
 * groups from GET admin/views, and sub-tabs that pass canTab(group, tab) —
 * checked against both `tab` and the snake_case of the name. The Admins &
 * access rows show to every admin (anyone may look; only super admins
 * change); the two Feature access rows only to super admins.
 */

export interface AdminTabEntry {
  group: string;
  /** Deep-link `tab` value; absent when the panel ignores `tab`. */
  tab?: string;
  /** The tab's name on screen. */
  name: string;
  /** "Email Center → Deliverability" */
  label: string;
  keywords: string[];
}

type Row = [name: string, tab: string | undefined, keywords?: string[]];

const group = (key: string, title: string, rows: Row[]): AdminTabEntry[] =>
  rows.map(([name, tab, keywords]) => ({
    group: key, tab, name, label: `${title} → ${name}`, keywords: keywords || [],
  }));

export const ADMIN_TABS: AdminTabEntry[] = [
  ...group('users', 'Users', [
    ['Directory', 'directory', ['accounts', 'profiles', 'find user', 'list']],
    ['Approvals', 'approvals', ['pending', 'approve', 'waitlist', 'invite requests']],
    ['Task queue', 'task_queue', ['tasks', 'queue', 'todo']],
    ['Add user', 'add_user', ['create', 'new user', 'new account']],
    ['Bulk add', 'bulk_add', ['import', 'csv', 'bulk accounts']],
    ['Sign-in', 'signin', ['google', 'linkedin', 'oauth', 'sso', 'auto create', 'no account found', 'sign up']],
    ['Send invites', 'send_invites', ['invite', 'invitation email']],
    ['Invitations', 'invitations', ['invites sent', 'referral']],
    ['LinkedIn cache', 'linkedin_cache', ['linkedin', 'profile enrich']],
    ['Deletion requests', 'deletion_requests', ['delete account', 'erase', 'gdpr', 'remove']],
    ['E-mail changes', 'email_changes', ['email change', 'e-mail change', 'change email', 'posthog', 'merge',
      'old email', 'current email', 'retired', 'release', 'log']],
    ['Contact limits', 'contact_limits', ['contact limit', 'university contacts', 'request more', 'quota',
      'trial limit', 'contact details', 'tto', 'view limit', 'grant', 'more contacts']],
    ['Feedback & bugs', 'feedback_bugs', ['feedback', 'bug report', 'complaints']],
  ]),
  ...group('user360', 'User 360', [
    ['Overview', 'overview', ['profile', 'summary', 'one user']],
    ['Timeline', 'timeline', ['activity', 'events', 'history']],
    ['Chats', 'chats', ['conversations', 'landing chat']],
    ['Messages', 'messages', ['emails sent', 'outreach messages']],
    ['Searches', 'searches', ['queries', 'search history']],
    ['Search performance', 'search_performance', ['latency', 'results quality']],
    ['Saved & outreach', 'saved_outreach', ['bookmarks', 'saved techs', 'contact requests']],
    ['Sessions', 'sessions', ['logins', 'devices', 'ip']],
  ]),
  ...group('tags', 'User tags', [
    ['Accounts', 'accounts', ['lifecycle', 'filter', 'export', 'prospect', 'trial']],
    ['Tags', 'tags', ['custom tags', 'assign', 'label']],
    ['Automatic tags', 'auto', ['auto', 'persona', 'rules']],
    ['Account origin', 'origin', ['bulk add', 'origin', 'batches', 'source', 'signup source']],
    ['Overview', 'overview', ['counts', 'lifecycle summary']],
  ]),
  ...group('analytics', 'Analytics', [
    ['Dashboard', undefined, ['growth', 'signups', 'kpi']],
    ['Month over month', undefined, ['mom', 'monthly', 'growth']],
    ['User analytics', undefined, ['engagement', 'profile completion']],
    ['Site visitors', undefined, ['visitors', 'traffic', 'ip geo', 'country']],
  ]),
  ...group('posthog', 'Product Analytics', [
    ['Overview', undefined, ['posthog', 'events']],
    ['Actions', undefined, ['utech_click', 'clicks', 'events']],
    ['Funnels', undefined, ['conversion', 'drop off']],
    ['People', undefined, ['persons', 'per-user activity']],
    ['Query', undefined, ['hogql', 'sql']],
  ]),
  ...group('search_intel', 'Search Intel', [
    ['Overview', undefined, ['search', 'queries']],
    ['Members', undefined, ['members', 'top users', 'top searchers', 'ranking', 'lifetime searches', 'profile']],
    ['Query explorer', undefined, ['query', 'explore']],
    ['Homepage searches', undefined, ['home', 'homepage']],
    ['Step timings', undefined, ['latency', 'timing', 'slow']],
    ['Search flow', undefined, ['pipeline', 'steps']],
    ['Search log', undefined, ['log', 'search_query_logs']],
    ['Top queries', undefined, ['popular', 'zero results', 'terms']],
    ['Bookmarks', undefined, ['saved', 'bookmark']],
    ['Per-user rollup', undefined, ['per user', 'rollup']],
  ]),
  ...group('homepage', 'Homepage', [
    ['Hero numbers', 'hero_numbers', ['homepage', 'hero', 'stats', 'metrics', 'counts', 'numbers', '+new',
      'new this month', 'new line', 'universities & labs', 'tech offers', 'countries', 'live count', 'manual number']],
    ['Trusted by', 'trusted_by', ['homepage', 'trusted by', 'logos', 'logo', 'banner', 'r&d leaders', 'clients',
      'customers', '...more', 'upload logo', 'scrolling', 'marquee']],
    ['Success stories', 'success_stories', ['homepage', 'success stories', 'cosmic connections', 'matchmaking',
      'case studies', 'testimonials']],
    ['Search chips', 'search_chips', ['homepage', 'search chips', 'chips', 'bubbles', 'surprise me', 'keywords',
      'suggested searches', 'battery storage ip', 'drug delivery patents', 'computer vision tech']],
  ]),
  ...group('onboarding', 'Onboarding', [
    ['Settings', 'settings', ['go live', 'roll back', 'flow', 'onboarding copy', 'first run', 'v1', 'v2',
      'explorer landing', 'entry modal', 'card depth', 'search lock', 'industry order', 'hide industry']],
    ['Industries & keywords', 'keywords', ['onboarding keywords', 'industry chips', 'first run chips', 'keyword chips',
      'step 2', 'corpus suggestions', 'reset to corpus']],
    ['Cosmos picks', 'picks', ['top 3', 'random pool', 'explorer landing', 'precompute', 'pin', 'exclude',
      'picked for you', 'thin lists', 'refresh picks']],
    ['Funnel', 'funnel', ['onboarding funnel', 'skip', 'drop off', 'time per step', 'abandon', 'heatmap',
      'chip position', 'trial origin', 'conversion']],
    ['People', 'people', ['onboarding people', 'journey', 'onboarding timeline']],
  ]),
  ...group('foryou_whatsnew', "For You & What's New", [
    ['For You · Settings', 'for_you_settings', ['for you', 'problem statements', 'interests', 'keywords', 'featured',
      'weekly pick', 'rollover', 'strong match', 'pool', 'share limits', 'saved technologies']],
    ['For You · Usage', 'for_you_usage', ['for you', 'usage', 'problem statements', 'keywords in use', 'shares',
      'recipients', 'featured clicks']],
    ['For You · Matching', 'for_you_matching', ['for you', 'matching', 're-match', 'rebuild pools', 'keyword pool',
      'queue', 'failures', 'llm relevance', 'recrawl']],
    ["What's New · News", 'what_s_new_news', ["what's new", 'whats new', 'news', 'in the world', 'licensing news',
      'ip news', 'curated', 'publish', 'draft', 'funding', 'partnerships', 'policy']],
    ["What's New · Settings", 'what_s_new_settings', ["what's new", 'whats new', 'platform updates', 'new universities',
      'ip added', 'ip removed', 'snapshot', 'corpus snapshot', 'categories', 'industries', 'type labels']],
    ["What's New · Usage", 'what_s_new_usage', ["what's new", 'whats new', 'usage', 'views', 'source clicks']],
    ['Feature access', 'feature_access', ['feature access', 'who can open', 'rollout', 'launch', 'go live', 'enable',
      'super admins only', 'selected admins', 'all admins', 'everyone', 'preview', 'beta', 'for you', 'new for you',
      "what's new", 'whats new', 'open page', 'member pages']],
  ]),
  ...group('leads', 'Leads & Marketing', [
    ['Prospects', 'prospects', ['prospects', 'fence sitters', 'upsell', 'upgrade', 'conversion', 'nudge', 'lead score',
      'trial lapsed', 'win-back', 'abandoned checkout', 'paywall', 'hot leads', 'follow up']],
    ['COMET', 'comet', ['comet']],
    ['Whitepaper', 'whitepaper', ['whitepaper', 'download']],
    ['Newsletter', 'newsletter', ['newsletter', 'subscribers']],
    ['Concierge', 'concierge', ['concierge', 'requests']],
    ['Contact University', 'contact_university', ['contact university', 'university contacts', 'contact tto',
      'opened contacts', 'contact opens', 'cta', 'lead alert', 'team alert', 'email on click', 'daily cap']],
    ['Talk to Concierge', 'talk_to_concierge', ['talk to concierge', 'concierge click', 'booking', 'cta',
      'lead alert', 'team alert', 'email on click', 'daily cap']],
    ['Interest', 'interest', ['interest', 'enquiries']],
    ['Early access', 'early_access', ['early access', 'waitlist']],
    ['List Your IP', 'list_your_ip', ['list your ip', 'submit ip']],
  ]),
  ...group('email', 'Email Center', [
    ['People', 'people', ['contacts', 'timeline', 'import', 'directory', 'address']],
    ['Dashboard', 'dashboard', ['email stats', 'email dashboard', 'weekly', 'open rate', 'click rate', 'ctor',
      'by template', 'per campaign', 'manual vs auto', 'primary cta', 'emails sent', 'memac analytics', 'individual sends']],
    ['Invites', 'invites', ['invite tracking', 'invitation emails', 'invited by admin', 'bulk add emails',
      'send invites', 'invite runs', 'bulk group', 'bulk upload analytics', 'signed in after invite', 'invite open rate']],
    ['MEMAC', 'memac', ['memac', 'triggers', 'lifecycle emails', 'engagement matrix', 'cohort', 'status', 'cadence',
      'automation', 'dry run', 'nudge', 'renewal reminder', 'win-back', 'unsubscribe']],
    ['Campaigns', 'campaigns', ['send', 'cohort', 'campaign history', 'funnel by tag', 'approved senders']],
    ['Email designs', 'email_designs', ['design', 'html', 'builder', 'layout']],
    ['Saved templates', 'saved_templates', ['saved templates', 'template library', 'reuse template', 'campaign template',
      'memac template']],
    ['Link clicks', 'link_clicks', ['clicks', 'urls', 'bitly']],
    ['Deliverability', 'deliverability', ['bounce', 'incidents', 'suppression', 'blocked', 'spam', 'resolve']],
    ['Delivery trace', 'trace', ['trace', 'what happened', 'why', 'bounce', 'otp', 'sign-in code', 'message id', 'address']],
    ['Failed sends', 'failed_sends', ['errors', 'failed', 'retry']],
    ['Conversations', 'conversations', ['threads', 'conversation id', 'otp-login', 'replies']],
    ['Tracking', 'tracking', ['opens', 'clicks', 'contact threads', 'invitations']],
    ['Delivery report', 'delivery_report', ['delivered', 'bounce rate', 'stats', 'sendgrid stats']],
    ['Researcher outreach', 'researcher_outreach', ['researcher campaign', 'composer', 'tech managers']],
    ['Sent log', 'sent_log', ['history', 'outbox', 'sent']],
    ['Campaign templates', 'campaign_templates', ['templates', 'outreach templates']],
    ['Manual send', 'manual_send', ['single email', 'compose', 'one-off']],
    ['Bulk mail-merge', 'bulk_mail_merge', ['csv', 'xlsx', 'mail merge']],
    ['SendGrid logs', 'sendgrid_logs', ['email logs', 'activity', 'sendgrid']],
    ['Templates (editable)', 'templates', ['customer templates', 'subject', 'sender', 'otp email']],
    ['OTP tools', 'otp_tools', ['sign-in code', 'login code', 'reproduce', 'otp']],
  ]),
  ...group('content', 'Content & TRL', [
    ['Overview', 'overview', ['corpus', 'technologies']],
    ['Sectors & keywords', 'sectors', ['pivot', 'sub-sector', 'subsector', 'sector', 'keyword', 'major keyword',
      'cosmos', 'taxonomy', 'industry', 'specialisation', 'specialization', 'white space', 'heatmap', 'insights',
      'maturity', 'crosstab']],
    ['Universities', 'universities', ['university', 'institutions']],
    ['Technologies', 'technologies', ['techs', 'corpus', 'tech detail']],
    ['Researchers', 'researchers', ['inventors', 'researcher']],
    ['Tech managers', 'tech_managers', ['tto', 'licensing officer']],
    ['Contact emails', 'contact_emails', ['contacts', 'emails']],
    ['Catalog DB', 'catalog_db', ['catalog', 'sqlite']],
    ['Categories', 'categories', ['category', 'industry']],
    ['Data quality', 'data_quality', ['missing', 'empty', 'quality']],
    ['TRL', 'trl', ['trl', 'readiness']],
    ['Crawl quality', 'crawl_quality', ['crawl', 'scrape']],
  ]),
  ...group('universities', 'Universities', [
    ['Global Universities', undefined, ['sheet', 'mapping', 'counts', 'contacts']],
    ['TRL provenance', undefined, ['trl', 'provenance']],
    ['Listing requests', undefined, ['listing', 'requests', 'add university']],
    ['Emails', undefined, ['emails', 'contacts', 'tto']],
  ]),
  ...group('ocean', 'Ocean', [
    ['Overview', undefined, ['ocean', 'crawl']],
    ['Companies', undefined, ['company']],
    ['Problems', undefined, ['problem statements']],
    ['Evidence', undefined, ['evidence']],
    ['Pages', undefined, ['pages', 'web']],
    ['Domains', undefined, ['domain']],
    ['Dashboards', undefined, ['dashboard']],
    ['Data & files', undefined, ['files', 'exports', 'data locations']],
    ['Schema', undefined, ['schema', 'tables']],
    ['SQL', undefined, ['sql', 'query']],
    ['COSMOS matching', 'cosmos_matching', ['cosmos matching', 'semantic matching', 'ocean match', 'problem match',
      'matches', 'embeddings']],
    ['Search engine', 'search_engine', ['ocean search', 'search engine', 'typesense', 'vectors', 'semantic index',
      'keyword index', 'reindex', 'index build', 'ai search', 'search settings', 'recent searches']],
    ['Feature access', 'feature_access', ['feature access', 'who can open', 'ocean', 'rollout', 'launch', 'go live',
      'enable', 'super admins only', 'selected admins', 'all admins', 'everyone', 'preview', 'beta', 'open page',
      'coming soon', 'member page']],
  ]),
  ...group('chat', 'Chat Stats', [
    ['Overview', undefined, ['chats', 'landing']],
    ['Monthly', undefined, ['month']],
    ['Daily & hourly', undefined, ['daily', 'hourly']],
    ['Returning users', undefined, ['returning', 'retention']],
    ['Sessions', undefined, ['sessions']],
    ['Excluded', undefined, ['excluded', 'internal']],
    ['Prompt cards', undefined, ['prompt cards', 'suggestions']],
  ]),
  ...group('labs', 'AI Labs', [
    ['TRL Lab v2', undefined, ['trl', 'lab', 'experiment']],
    ['Horizon Sandbox', undefined, ['horizon', 'sandbox', 'experiment']],
  ]),
  ...group('aws', 'AWS & API spend', [
    ['Overview', 'overview', ['aws', 'amazon', 'cloud bill', 'credits left', 'aws credit', 'monthly spend', 'forecast', 'projects']],
    ['Invoices', 'invoices', ['aws invoice', 'bill', 'pdf', 'gst', 'tax invoice']],
    ['Costs', 'costs', ['cost explorer', 'by service', 'ec2 cost', 'rds cost', 'usage type', 'daily cost']],
    ['Resources', 'resources', ['ec2', 'rds', 'aurora', 's3', 'amplify', 'ecs', 'load balancer', 'elastic ip', 'rndlist', 'r&d list', 'config', 'instances', 'servers', 'database']],
    ['Health', 'health', ['status checks', 'alarms', 'cpu', 'idle', 'down']],
    ['API spend', 'api_spend', ['spend', 'replicate', 'credits', 'top up', 'api keys', 'members cost', 'horizon cost', 'manual cost', 'llm cost', 'cost per search', 'per search', 'running average']],
  ]),
  ...group('horizon', 'Horizon data', [
    ['Overview', 'overview', ['horizon', 'coverage']],
    ['Technologies', 'technologies', ['techs', 'coverage', 'have', 'pending']],
    ['Results', 'results', ['outputs', 'confidence', 'review', 'approve']],
    ['Runs', 'runs', ['batch', 'jobs']],
    ['Prompts', 'prompts', ['prompt', 'source', 'final prompt']],
    ['Pending', 'pending', ['missing', 'todo', 'queue']],
  ]),
  ...group('billing', 'Billing', [
    ['Users & subscriptions', 'users', ['subscription', 'plan', 'member', 'paid']],
    ['Cancellations & refunds', 'trials', ['refund', 'cancel', 'cancellation', 'trial']],
    ['Plans & pricing', 'plans', ['price', 'plan', 'currency']],
    ['Promotions & trials', 'promos', ['offer', 'promo', 'discount', 'trial20', 'win-back']],
    ['Upgrade prompts', 'prompts', ['paywall', 'upsell', 'upgrade']],
    ['Coupons', 'coupons', ['coupon', 'discount code']],
    ['Payments', 'payments', ['transactions', 'charges', 'invoice']],
    ['Razorpay', 'razorpay', ['gateway', 'razorpay subscriptions']],
    ['Gateway', 'gateway', ['webhook', 'mandate', 'ghost']],
    ['Emails', 'emails', ['billing emails', 'receipt', 'welcome voyager', 'razorpay emails', 'failed payment alert']],
  ]),
  ...group('ip_intel', 'IP Intel Report', [
    ['Overview', 'overview', ['ip intel', 'report']],
    ['Orders', 'orders', ['purchases', 'report orders', 'delivery']],
    ['Sample leads', 'sample_leads', ['sample', 'leads']],
    ['Bulk enquiries', 'bulk_enquiries', ['bulk', 'enquiry']],
    ['Activity', 'activity', ['page activity', 'views']],
    ['Pricing', 'pricing', ['price']],
    ['Coupons', 'coupons', ['coupon', 'discount']],
    ['Samples', 'samples', ['sample report']],
    ['Emails', 'emails', ['emails', 'notifications']],
  ]),
  ...group('tech_pages', 'Tech URL pages', [
    ['Overview', 'overview', ['traffic', 'public pages']],
    ['Tech pages', 'tech', ['technology page', 'url', 'views', 'cta']],
    ['University pages', 'university', ['university page', 'institution page']],
    ['Shares', 'shares', ['share links', 'shared']],
    ['Sections & access', 'sections', ['cta', 'sign-in state', 'paywall', 'access']],
    ['URLs & SEO', 'urls', ['slug', 'seo', 'url pattern', 'sitemap']],
    ['Labels & carousel', 'labels', ['carousel', 'labels']],
  ]),
  ...group('recrawl', 'Recrawl', [
    ['Overview', 'overview', ['recrawl', 're-crawl', 'auto crawl', 'crawl', 'scrape', 'scraper', 'attention', 'approvals',
      'awaiting approval', 'held', 'queue', 'running', 'next runs', 'health', 'drift']],
    ['Universities', 'universities', ['recrawl', 'university', 'schedule', 'every n days', 'throttle', 'workers',
      'rate limit', 'probe', 'dry run', 'run now', 'pause', 'upload', 'manual file', 'manifest', 'risk', 'cleanup flags']],
    ['Flow', 'flow', ['recrawl', 'pipeline', 'flow', 'steps', 'scenario', 'replay', 'graph', 'scraper steps']],
    ['Runs', 'runs', ['recrawl', 'run history', 'approve', 'reject', 'cancel', 'resume', 'staged changes', 'log', 'backup',
      'guard', 'partial scrape', 'cost']],
    ['Changes', 'changes', ['recrawl', 'timeline', 'added', 'modified', 'removed', 're-added', 'technology history',
      'versions', 'diff', 'leaderboard', 'coverage', 'master']],
    ['Settings', 'settings', ['recrawl', 'scheduler', 'kill switch', 'RECRAWL_AUTO', 'auto-approve', 'budget', 'enrichment',
      'quiet hours', 'cpu', 'memory', 'post-load', 'reconcile', 'backfill']],
    ['Guide', 'guide', ['recrawl', 'how it works', 'add a university', 'new university', 'troubleshooting', 'documentation']],
  ]),
  ...group('access', 'Admins & access', [
    ['Admin accounts', 'accounts', ['admin accounts', 'add admin', 'remove admin', 'team member', 'team account',
      'read-only', 'read only', 'preview', 'role', 'billing_admins', 'who is admin']],
    ['History', 'history', ['admin history', 'who added', 'added by', 'made admin', 'assigned admin', 'removed admin',
      'role change', 'admin log']],
    ['Admin tabs', 'tabs', ['access control', 'permissions', 'admin access', 'who sees', 'hide tab', 'show tab',
      'enable tab', 'selected admins', 'all admins', 'super admin', 'new tabs', 'roles', 'groups', 'sub-tabs']],
  ]),
  ...group('database', 'Database', [
    ['Overview', undefined, ['aws', 'postgres']],
    ['Tables', undefined, ['tables', 'row counts']],
    ['Schema', undefined, ['schema', 'columns']],
    ['Browse rows', undefined, ['rows', 'browse']],
    ['Query console', undefined, ['sql', 'query']],
    ['University mapping', undefined, ['mapping', 'university key']],
  ]),
  ...group('system', 'System', [
    ['Sessions', undefined, ['sessions', 'logins']],
    ['Landing limits', undefined, ['limits', 'rate limit']],
    ['Database', undefined, ['database', 'db']],
    ['SQL console', undefined, ['sql', 'query']],
    ['Catalog DB', undefined, ['catalog', 'sqlite']],
    ['Jobs', undefined, ['background jobs', 'scheduler', 'cron']],
    ['Search indexes', undefined, ['typesense', 'pinecone', 'algolia', 'reindex', 're-index', 'rebuild', 'index',
      'new university', 'university load', 'corpus', 'sync', 'missing', 'similar technologies', 'filter lists',
      'caches']],
    ['Integrations', undefined, ['integrations', 'api keys status', 'sendgrid', 'razorpay']],
  ]),
];

/** The deep link an entry opens: the group, plus its tab when the panel reads one. */
export const tabLink = (e: AdminTabEntry): AdminTarget =>
  (e.tab ? { group: e.group, tab: e.tab } : { group: e.group });
